import React, { useRef, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { ReactComponent as UploadIcon } from "@/assets/upload/uploader_icon.svg?react";
import { ReactComponent as LinkIcon } from "@/assets/upload/link_icon.svg?react";
import { ReactComponent as SendIcon } from "@/assets/upload/send_icon.svg?react";
import { useUploadStore } from "@/store/uploadStore";
import { useProcessVuStore } from "@/store/processVuStore";
import { useProcessVhStore } from "@/store/processVhStore";
import UploadText from "./components/UploadText";
import { designTokens } from "@/styles/designTokens";
import { ProcessResult } from "types/output_vu";
import { ProcessResultVh } from "types/output_vh";
import { useNotificationStore } from "store/notificationStore";

// --- Styled components -------------------------------------------------------

interface UploadSectionProps {
  $isDragging: boolean;
  $isProcessing: boolean;
}

const UploadSection = styled.div<UploadSectionProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 40px 40px;
  gap: 60px;
  width: 100%;
  min-height: 288px;
  background: ${(props) =>
    props.$isDragging ? "rgba(255, 255, 255, 0.8)" : "rgba(255, 255, 255)"};
  box-shadow: ${(props) =>
    props.$isDragging
      ? "0px 0px 20px rgba(58, 111, 133, 0.3)"
      : "0px 0px 14px rgba(58, 111, 133, 0.2)"};
  backdrop-filter: blur(8px);
  border-radius: 8px;
  cursor: ${(props) => (props.$isProcessing ? "not-allowed" : "pointer")};
  transition: all 0.2s ease-in-out;
  transform: ${(props) => (props.$isDragging ? "scale(1.01)" : "scale(1)")};
  opacity: ${(props) => (props.$isProcessing ? 0.8 : 1)};
`;

const IconTextGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 336px;
  height: 64px;
`;

const UploadBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #727272;
  border-radius: 8px;
  min-width: 400px;
  width: 100%;

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    background: transparent;
    color: #33353d;

    &::placeholder {
      color: #a5a5ae;
    }
  }
`;

const UploadButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  background: #000;
  border-radius: 20px;
  border: none;
  padding: 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.2s ease-in-out;

  &:disabled {
    background: ${designTokens.colors.font.tertiary};
  }
`;

// --- Logic ------------------------------------------------------------------

interface FileUploaderProps {
  projectId: string;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

const validateFile = (file: File): { isValid: boolean; error?: string } => {
  if (
    ![
      "video/mp4",
      "audio/mp3",
      "audio/mpeg",
      "audio/wav",
      "audio/aac",
      "audio/flac",
    ].includes(file.type)
  ) {
    return {
      isValid: false,
      error: "MP4, MP3, WAV, AAC, FLAC 파일만 업로드 가능합니다.",
    };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { isValid: false, error: "파일 크기는 최대 2GB까지 가능합니다." };
  }
  return { isValid: true };
};

// --- Component --------------------------------------------------------------

const FileUploader: React.FC<FileUploaderProps> = React.memo(
  ({ projectId }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [localFile, setLocalFile] = useState<File | null>(null);
    const [youtubeUrl, setYoutubeUrl] = useState("");
    const [lastRequest, setLastRequest] = useState<{ file: File | null; url: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hasShownErrorModal = useRef(false); // ✅ prevent infinite loop

    const {
      handleSubmit,
      progress,
      isLoading,
      result,
      error,
      resetState,
      resetError,
    } = useUploadStore((state) => state);
    
    // 디버깅: 스토어 상태 확인
    console.log("🔍 FileUploader store state:", { 
      result: result, 
      progress: progress, 
      isLoading: isLoading 
    });
    const resetVuResult = useProcessVuStore((state) => state.reset);
    const setVuResult = useProcessVuStore((state) => state.setResult);
    const resetVhResult = useProcessVhStore((state) => state.reset);
    const setVhResult = useProcessVhStore((state) => state.setResult);
    const { showNotification, showErrorNotification } = useNotificationStore();

    const isUploading = progress.step === "upload" || progress.step === "download";
    const isProcessing = ["upload", "download"].includes(progress.step) || isLoading;

    // --- 업로드 처리 --------------------------------------------------------
    const handleFormSubmit = useCallback(
      async (fileToUpload: File | null, urlToUpload: string) => {
        setLastRequest({ file: fileToUpload, url: urlToUpload });
        resetVuResult();
        resetVhResult();

        if (urlToUpload) {
          const youtubeRegex =
            /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
          if (!youtubeRegex.test(urlToUpload.trim())) {
            alert("올바른 유튜브 URL을 입력해주세요.");
            return;
          }
        }

        if (!fileToUpload && !urlToUpload) {
          alert("파일을 선택하거나 URL을 입력해주세요.");
          return;
        }

        await handleSubmit({
          file: fileToUpload ?? undefined,
          youtubeUrl: urlToUpload,
          projectId: projectId,
        });
      },
      [projectId, handleSubmit, resetVuResult, resetVhResult]
    );

    // 업로드 결과 알림은 전역 UploadStore에서 한 번만 트리거됩니다.

    // --- 에러 시 전역 알림 띄우기 -----------------------------------------------
    useEffect(() => {
      if (error && !hasShownErrorModal.current) {
        hasShownErrorModal.current = true;

        showErrorNotification({
          title: "업로드 실패",
          message: `오류가 발생했습니다: ${error}\n재시도 하시겠습니까?`,
          onRetry: () => {
            handleRetry();
            hasShownErrorModal.current = false;
          },
          onCancel: () => {
            handleCancelRetry();
            hasShownErrorModal.current = false;
          },
        });
      }
    }, [error, showErrorNotification]);

    // --- 상태 초기화 -------------------------------------------------------
    useEffect(() => {
      if (progress.step === "idle") {
        setLastRequest(null);
        setLocalFile(null);
        setYoutubeUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    }, [progress.step]);

    // --- 재시도/취소 -------------------------------------------------------
    const handleRetry = () => {
      if (lastRequest) {
        resetError();
        handleFormSubmit(lastRequest.file, lastRequest.url);
      }
    };

    const handleCancelRetry = () => {
      resetError();
      setLastRequest(null);
    };

    // --- 파일 검증 및 처리 --------------------------------------------------
    const handleFileValidation = useCallback(
      (selectedFile: File) => {
        const { isValid, error } = validateFile(selectedFile);
        if (isValid) {
          setLocalFile(selectedFile);
          setYoutubeUrl("");
          handleFormSubmit(selectedFile, "");
        } else if (error) {
          alert(error);
        }
      },
      [handleFormSubmit]
    );

    // --- Drag & Drop -------------------------------------------------------
    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      e.preventDefault();
      e.stopPropagation();
    }, [isProcessing]);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, [isProcessing]);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, [isProcessing]);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) handleFileValidation(files[0]);
    }, [isProcessing, handleFileValidation]);

    // --- Input & URL 처리 --------------------------------------------------
    const handleFileClick = useCallback(() => {
      if (!isProcessing) fileInputRef.current?.click();
    }, [isProcessing]);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (isProcessing) return;
      const files = e.target.files;
      if (files && files.length > 0) handleFileValidation(files[0]);
    }, [isProcessing, handleFileValidation]);

    const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (isProcessing) return;
      setYoutubeUrl(e.target.value);
      if (e.target.value) setLocalFile(null);
    }, [isProcessing]);

    const handleUrlSubmit = useCallback(() => {
      if (isProcessing || !youtubeUrl.trim()) return;
      handleFormSubmit(null, youtubeUrl);
    }, [isProcessing, youtubeUrl, handleFormSubmit]);

    // --- Render ------------------------------------------------------------
    return (
      <UploadSection
        onClick={handleFileClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        $isDragging={isDragging}
        $isProcessing={isProcessing}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".mp4,.mp3,.wav,.aac,.flac"
          style={{ display: "none" }}
          disabled={isProcessing}
        />
        <IconTextGroup>
          <UploadIcon style={{ width: "64px", height: "64px" }} />
          <UploadText
            isUploading={isUploading}
            percent={progress.percent}
            message={progress.message}
          />
        </IconTextGroup>
        <UploadBox onClick={(e) => e.stopPropagation()}>
          <LinkIcon style={{ width: "24px", height: "24px" }} />
          <input
            type="text"
            value={youtubeUrl}
            onChange={handleUrlChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleUrlSubmit();
              }
            }}
            placeholder="유튜브 URL을 입력하세요."
            disabled={isProcessing}
          />
          <UploadButton
            onClick={handleUrlSubmit}
            disabled={isProcessing || !youtubeUrl.trim()}
          >
            <SendIcon style={{ width: "32px", height: "32px" }} />
          </UploadButton>
        </UploadBox>
      </UploadSection>
    );
  }
);

FileUploader.displayName = "FileUploader";
export default FileUploader;
